import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Heart, Building2, MapPin, BarChart3,
  ChevronLeft, ChevronRight, Sparkles, ArrowUpRight, Crown, Check, Star
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { FeaturedPropertiesCarousel } from "@/components/FeaturedPropertiesCarousel";
import { PropertyDetailModal } from "@/components/PropertyDetailModal";
import { MASTER_PARTNERS } from "@/constants/masterPartners";
import { Breadcrumbs } from "@/components/Breadcrumbs";

const PROPERTY_1 = "https://images.unsplash.com/photo-1670589953882-b94c9cb380f5?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
const PROPERTY_2 = "https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

const PILLARS = [
  { name: "Emocional", weight: 35, icon: Heart, desc: "Sensaciones, ambiente y estilo de vida que te hacen sentir en casa." },
  { name: "Técnico", weight: 25, icon: Building2, desc: "Distribución, instalaciones y características físicas del inmueble." },
  { name: "Urbano", weight: 20, icon: MapPin, desc: "Ubicación, servicios cercanos, seguridad y conectividad de la zona." },
  { name: "Financiero", weight: 20, icon: BarChart3, desc: "Precio, mantenimiento, plusvalía y potencial de inversión." },
];

const STEPS = [
  {
    n: "01",
    icon: Heart,
    title: "Construye tu ADN de Hogar",
    description: "Responde 52 preguntas psicográficas divididas en 4 olas: emocional, técnica, urbana y financiera. Esto crea tu perfil único de preferencias.",
  },
  {
    n: "02",
    icon: Star,
    title: "Desliza y Haz Match",
    description: "Solo verás propiedades con 70%+ de compatibilidad. Desliza a la derecha para dar like, a la izquierda para pasar. ¡Así de simple!",
  },
  {
    n: "03",
    icon: Sparkles,
    title: "Conecta y Visita",
    description: "Al hacer match, el agente te contactará. La dirección exacta se revela solo después de firmar un acuerdo de confidencialidad.",
  },
];

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#CCD500]">
    <span className="h-px w-8 bg-[#CCD500]/60" /> {children}
  </span>
);

const MasterPartnerCard = ({ p, onClick }) => (
  <motion.div
    whileHover={{ y: -6 }}
    onClick={onClick}
    className="relative shrink-0 w-[320px] rounded-3xl overflow-hidden bg-slate-900/60 backdrop-blur-xl border border-white/10 hover:border-[#CCD500]/40 transition-colors group cursor-pointer"
    data-testid={`buyer-mp-card-${p.slug}`}
  >
    <div className="relative h-44 overflow-hidden">
      <img src={p.img} alt={p.city} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />
      <div className="absolute top-3 left-3">
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded-full bg-[#CCD500] text-black">{p.tag}</span>
      </div>
      <div className="absolute bottom-3 left-4">
        <p className="text-xs text-slate-300 uppercase tracking-wider">{p.city}</p>
        <h4 className="font-display text-2xl font-bold text-white leading-tight">{p.mp}</h4>
      </div>
    </div>
    <div className="p-4 flex items-center justify-between">
      <div className="flex -space-x-2">
        <img src={PROPERTY_1} alt="" className="w-10 h-10 rounded-lg border-2 border-slate-900 object-cover" />
        <img src={PROPERTY_2} alt="" className="w-10 h-10 rounded-lg border-2 border-slate-900 object-cover" />
        <div className="w-10 h-10 rounded-lg border-2 border-slate-900 bg-slate-800 flex items-center justify-center text-[10px] font-bold text-[#CCD500]">+{p.props}</div>
      </div>
      <span className="text-xs font-semibold text-[#CCD500] flex items-center gap-1 group-hover:gap-2 transition-all">
        Explorar <ArrowUpRight className="w-3.5 h-3.5" />
      </span>
    </div>
  </motion.div>
);

const MasterPartnersCarousel = () => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const scrollBy = (dx) => scrollRef.current && scrollRef.current.scrollBy({ left: dx, behavior: "smooth" });
  return (
    <div className="relative">
      <div
        ref={scrollRef}
        className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
        style={{ scrollbarWidth: "none" }}
        data-testid="buyer-mp-carousel"
      >
        <style>{`.scrollbar-hide::-webkit-scrollbar{display:none;}`}</style>
        {MASTER_PARTNERS.map((p) => (
          <div key={p.slug} className="snap-start">
            <MasterPartnerCard p={p} onClick={() => navigate(`/partner/${p.slug}`)} />
          </div>
        ))}
      </div>
      <div className="hidden md:flex absolute -top-16 right-0 gap-2">
        <button
          onClick={() => scrollBy(-360)}
          data-testid="buyer-mp-prev"
          className="w-11 h-11 rounded-full bg-white/5 border border-white/10 hover:bg-[#CCD500] hover:text-black text-white flex items-center justify-center transition-all"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={() => scrollBy(360)}
          data-testid="buyer-mp-next"
          className="w-11 h-11 rounded-full bg-white/5 border border-white/10 hover:bg-[#CCD500] hover:text-black text-white flex items-center justify-center transition-all"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export const BuyerLandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showPropertyModal, setShowPropertyModal] = useState(false);

  useEffect(() => {
    if (user && user.role === "shopper") navigate("/swipe");
  }, [user, navigate]);

  const goToRegister = () => navigate("/auth");

  const handleViewProperty = (property) => {
    setSelectedProperty(property);
    setShowPropertyModal(true);
  };

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100 font-body selection:bg-[#CCD500] selection:text-black">
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0B1120]/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
            data-testid="buyer-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Volver al inicio</span>
          </button>
          <img
            src="https://customer-assets.emergentagent.com/job_propswipe-7/artifacts/65kacgp6_Isotipo%20Findperty-2.png"
            alt="Findperty"
            className="h-8 brightness-0 invert"
          />
          <Button
            onClick={goToRegister}
            className="bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] hover:scale-105 transition-all rounded-full px-5"
            data-testid="buyer-header-cta"
          >
            Empezar gratis
          </Button>
        </div>
      </header>

      {/* Breadcrumbs */}
      <div className="pt-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Breadcrumbs items={[{ label: "Para Compradores" }]} />
        </div>
      </div>

      {/* Featured Properties Carousel (full-width hero) */}
      <div className="pt-4">
        <FeaturedPropertiesCarousel onViewProperty={handleViewProperty} onRegister={goToRegister} />
      </div>

      {/* Buyer Hero Intro */}
      <section className="relative py-16 md:py-24 px-6 lg:px-8 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-[#CCD500]/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-20 -right-40 w-[500px] h-[500px] bg-rose-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-5xl mx-auto text-center">
          <Eyebrow>Para Compradores</Eyebrow>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] mt-6" data-testid="buyer-hero-title">
            Tu hogar ideal{" "}
            <span className="bg-gradient-to-r from-[#CCD500] via-[#E5FF00] to-[#CCD500] bg-clip-text text-transparent">
              te está esperando
            </span>.
          </h1>
          <p className="mt-7 text-lg md:text-xl text-slate-400 max-w-2xl mx-auto">
            El primer sistema de emparejamiento psicográfico inmobiliario. 52 puntos de compatibilidad para encontrar la propiedad que realmente se ajusta a tu estilo de vida.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              onClick={goToRegister}
              data-testid="buyer-hero-cta"
              className="bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] hover:scale-105 transition-all shadow-[0_0_30px_rgba(204,213,0,0.4)] rounded-full px-7 h-14 text-base"
            >
              Crear mi cuenta gratis
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="buyer-hero-how-cta"
              className="bg-white/5 text-white border-white/20 hover:bg-white/10 hover:border-white/40 rounded-full px-7 h-14 text-base backdrop-blur-md"
            >
              Ver cómo funciona
            </Button>
          </div>
          <div className="mt-10 flex flex-wrap gap-6 justify-center items-center">
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <Check className="w-4 h-4 text-[#CCD500]" /> Sin tarjeta de crédito
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <Check className="w-4 h-4 text-[#CCD500]" /> Setup en 5 minutos
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <Check className="w-4 h-4 text-[#CCD500]" /> 100% gratis para compradores
            </div>
          </div>
        </div>
      </section>

      {/* CÓMO FUNCIONA */}
      <section id="how-it-works" className="relative py-20 md:py-28 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Eyebrow>Proceso Simple</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4 tracking-tight">
              Cómo funciona <span className="text-[#CCD500]">Findperty</span>
            </h2>
            <p className="mt-4 text-slate-400">
              Un proceso intuitivo en tres pasos para encontrar tu hogar perfecto.
            </p>
          </div>
          <div className="relative grid md:grid-cols-3 gap-8">
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-[#CCD500]/40 to-transparent" />
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative text-center"
                data-testid={`buyer-step-${s.n}`}
              >
                <div className="relative mx-auto w-24 h-24 rounded-full bg-slate-900 border border-white/10 flex items-center justify-center mb-6 z-10">
                  <span className="font-display text-2xl font-black text-[#CCD500]">{s.n}</span>
                  <div className="absolute inset-0 rounded-full border border-[#CCD500]/30 animate-ping opacity-20" />
                </div>
                <div className="w-12 h-12 mx-auto rounded-2xl bg-[#CCD500]/10 border border-[#CCD500]/30 flex items-center justify-center mb-4">
                  <s.icon className="w-6 h-6 text-[#CCD500]" />
                </div>
                <h3 className="font-display text-xl font-bold text-white mb-3">{s.title}</h3>
                <p className="text-sm text-slate-400 max-w-xs mx-auto leading-relaxed">{s.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 PILARES DEL MATCH SCORE */}
      <section className="relative py-20 md:py-28 px-6 lg:px-8 bg-gradient-to-b from-transparent via-slate-950/40 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <Eyebrow>La Ciencia del Match</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4 tracking-tight">
              Los 4 Pilares del <span className="text-[#CCD500]">Match Score</span>
            </h2>
            <p className="mt-4 text-slate-400">
              Nuestro algoritmo evalúa 52 puntos de compatibilidad divididos en 4 categorías ponderadas.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="group relative rounded-3xl bg-slate-900/60 backdrop-blur-md border border-white/10 p-6 hover:border-[#CCD500]/40 transition-all"
                data-testid={`buyer-pillar-${p.name.toLowerCase()}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-11 h-11 rounded-2xl bg-[#CCD500]/10 border border-[#CCD500]/30 flex items-center justify-center">
                    <p.icon className="w-5 h-5 text-[#CCD500]" />
                  </div>
                  <span className="font-display text-3xl font-black text-white">
                    {p.weight}<span className="text-[#CCD500]">%</span>
                  </span>
                </div>
                <h3 className="font-display text-xl font-bold text-white mb-2">{p.name}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{p.desc}</p>
                <div className="mt-5 h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${p.weight}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: 0.2 + i * 0.08 }}
                    className="h-full bg-gradient-to-r from-[#CCD500] to-[#E5FF00]"
                  />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Visual umbral explanation */}
          <div className="mt-12 grid lg:grid-cols-2 gap-8 items-center rounded-3xl bg-slate-900/60 backdrop-blur-md border border-white/10 p-8 md:p-12">
            <div>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-3">
                Solo ves lo que <span className="text-[#CCD500]">realmente te interesa</span>
              </h3>
              <p className="text-slate-400 mb-5 leading-relaxed">
                Las propiedades con Match Score menor a 70% no aparecen en tu feed. Menos tiempo perdido, más conexiones significativas.
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-[#CCD500]" />
                  <span className="text-sm text-slate-300">70%+ Visible</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-slate-700" />
                  <span className="text-sm text-slate-300">&lt;70% Oculto</span>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative w-48 h-48">
                <svg className="w-full h-full transform -rotate-90">
                  <circle cx="96" cy="96" r="80" stroke="rgba(255,255,255,0.08)" strokeWidth="16" fill="none" />
                  <motion.circle
                    cx="96" cy="96" r="80"
                    stroke="#CCD500" strokeWidth="16" fill="none" strokeLinecap="round"
                    initial={{ strokeDasharray: `0 ${2 * Math.PI * 80}` }}
                    whileInView={{ strokeDasharray: `${0.7 * 2 * Math.PI * 80} ${2 * Math.PI * 80}` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2 }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="font-display text-4xl font-black text-white">70%</span>
                  <span className="text-xs text-slate-400 uppercase tracking-wider mt-1">Umbral mínimo</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MASTER PARTNERS CAROUSEL */}
      <section id="partners" className="relative py-20 md:py-28 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <Eyebrow>Red Nacional</Eyebrow>
              <h2 className="font-display text-4xl md:text-5xl font-bold mt-4 tracking-tight">
                Master Partners <span className="text-[#CCD500]">en 10 ciudades</span>.
              </h2>
              <p className="mt-4 text-slate-400 max-w-2xl">
                Líderes locales con territorio exclusivo. Cada Master Partner cura propiedades premium y opera su red de agentes verificados.
              </p>
            </div>
          </div>
          <MasterPartnersCarousel />
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="px-6 lg:px-8 pb-24">
        <div className="max-w-5xl mx-auto rounded-3xl bg-gradient-to-br from-[#CCD500] to-[#9DAB00] p-10 md:p-16 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/20 blur-3xl" />
          <div className="relative">
            <Crown className="w-10 h-10 text-black mx-auto mb-4" />
            <h2 className="font-display text-4xl md:text-6xl font-black text-black tracking-tighter mb-4 leading-[0.95]">
              ¿Listo para tu match?
            </h2>
            <p className="text-black/70 text-lg max-w-xl mx-auto mb-8">
              Únete gratis a Findperty. Compradores 100% gratis para siempre. Sin tarjeta, sin compromisos.
            </p>
            <Button
              size="lg"
              onClick={goToRegister}
              data-testid="buyer-final-cta"
              className="bg-black text-[#CCD500] font-bold hover:bg-slate-900 hover:scale-105 rounded-full px-8 h-14 text-base"
            >
              Crear mi cuenta gratis
              <Heart className="w-5 h-5 ml-2 fill-[#CCD500]" />
            </Button>
            <p className="text-xs text-black/60 mt-6">Sin tarjeta de crédito • Setup en 5 minutos</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 px-6 lg:px-8 py-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img
            src="https://customer-assets.emergentagent.com/job_propswipe-7/artifacts/65kacgp6_Isotipo%20Findperty-2.png"
            alt="Findperty"
            className="h-8 brightness-0 invert opacity-60"
          />
          <p className="text-sm text-slate-500">© 2026 Findperty · Hecho para compradores exigentes</p>
          <div className="flex gap-6 text-sm text-slate-400">
            <a href="#" className="hover:text-white transition-colors">Privacidad</a>
            <a href="#" className="hover:text-white transition-colors">Términos</a>
            <a href="#" className="hover:text-white transition-colors">Contacto</a>
          </div>
        </div>
      </footer>

      {/* Property Detail Modal */}
      <PropertyDetailModal
        property={selectedProperty}
        open={showPropertyModal}
        onOpenChange={setShowPropertyModal}
      />
    </div>
  );
};

export default BuyerLandingPage;
